<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div class="editor-dialog">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center py-2 px-0">
          <span class="primary--text">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>
      <v-card-text class="pa-4">
        <label class="label-input">Section Name</label>
        <v-row>
          <v-col cols="12" md="8"><v-text-field v-model="fields.title" label="Name"/></v-col>
          <v-col cols="12" md="4"><v-checkbox v-model="fields.enableAnchor" label="Enable Anchor"/></v-col>
        </v-row>

        <label class="label-input">Image</label>
        <div class="group-item">
          <v-row>
            <v-col cols="12" md="6"><v-checkbox v-model="fields.image.show" label="Show image" hide-details/></v-col>
            <v-col cols="12" md="6"
              ><v-select v-model="fields.image.position" :items="positionOptions" label="Image position"
            /></v-col>
          </v-row>
          <div v-if="fields.image.show" class="mt-4">
            <UploadImage
              :image="{ imageSrc: fields.image.src }"
              :height="'240'"
              :typeUpload="'image'"
              @callback="handleUploadImage"
              @callbackDelete="handleRemoveImage"
            />
            <v-text-field v-model="fields.image.alt" class="mt-4" label="Image alt" hide-details />
          </div>
        </div>

        <div class="mt-6">
          <label class="label-input">Header</label>
          <ckeditor :editor="editor" v-model="fields.header" :config="editorConfig" />
        </div>

        <v-row>
          <v-col md="6" cols="12" v-for="card in cardOptions" :key="card.key" class="mt-6">
            <label class="label-input">{{ card.label }} card</label>
            <div class="group-item">
              <v-row>
                <v-col cols="12">
                  <ColorPicker
                    :value="fields[card.key].backgroundColor"
                    :name="`${card.key}-card-background`"
                    label="Card background"
                    :default-color="card.defaultBackground"
                    @input="value => updateCard(card.key, 'backgroundColor', value)"
                  />
                </v-col>
                <v-col cols="12">
                  <ColorPicker
                    :value="fields[card.key].titleColor"
                    :name="`${card.key}-card-title`"
                    label="Title color"
                    :default-color="card.defaultTitleColor"
                    @input="value => updateCard(card.key, 'titleColor', value)"
                  />
                </v-col>
                <v-col cols="12">
                  <ColorPicker
                    :value="fields[card.key].itemBackgroundColor"
                    :name="`${card.key}-item-background`"
                    label="Item content background"
                    default-color="#FFFFFF"
                    @input="value => updateCard(card.key, 'itemBackgroundColor', value)"
                  />
                </v-col>
                <v-col cols="12">
                  <ColorPicker
                    :value="fields[card.key].itemTextColor"
                    :name="`${card.key}-item-text`"
                    label="Item content text color"
                    default-color="#242424"
                    @input="value => updateCard(card.key, 'itemTextColor', value)"
                  />
                </v-col>
              </v-row>

              <label class="d-block mt-4 mb-2">Card image</label>
              <UploadImage
                :image="{ imageSrc: fields[card.key].image.src }"
                :height="'160'"
                :typeUpload="'image'"
                @callback="obj => handleUploadCardImage(card.key, obj)"
                @callbackDelete="() => handleRemoveCardImage(card.key)"
              />
              <v-text-field v-model="fields[card.key].image.alt" class="mt-4" label="Image alt" hide-details />

              <label class="d-block mt-6 mb-2">Title</label>
              <ckeditor :editor="editor" v-model="fields[card.key].title" :config="editorConfig" />

              <div class="d-flex justify-space-between align-center mt-6 mb-4">
                <label class="label-input mb-0">Items</label>
                <v-btn small color="primary" @click="handleAddCardItem(card.key)"
                  ><v-icon size="16">mdi-plus</v-icon>Add Item</v-btn
                >
              </div>
              <v-expansion-panels accordion>
                <v-expansion-panel v-for="(item, index) in fields[card.key].items" :key="`${card.key}-${index}`">
                  <v-expansion-panel-header>
                    <div class="d-flex justify-space-between align-center flex-grow-1">
                      <span>{{ getItemLabel(item, index) }}</span>
                      <v-btn icon @click.stop="handleRemoveCardItem(card.key, index)"
                        ><v-icon color="#ff6c6c">mdi-delete</v-icon></v-btn
                      >
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <ckeditor
                      :editor="editor"
                      :value="item"
                      :config="editorConfig"
                      @input="value => updateCardItem(card.key, index, value)"
                    />
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div> </v-col
        ></v-row>

        <div class="mt-6">
          <ButtonPrimary :button="fields.button" :showIconPrefix="false" :showIconSuffix="false" :showSubBtn="false" />
        </div>

        <div class="mt-6">
          <label class="label-input">Footer</label>
          <ckeditor :editor="editor" v-model="fields.footer" :config="editorConfig" />
        </div>

        <SectionSettingsContainerFuild
          class="mt-6"
          :settings="fields.settings"
          :containerPage="fields.containerPage"
          :showBackgroundImage="false"
          :showContainerPage="false"
        />
      </v-card-text>
      <div class="footer-modal">
        <v-card-actions class="py-2 px-0">
          <v-spacer />
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { updateHtmlFromData } from '../common/html/update';
import ButtonPrimary from './common/button-primary.vue';
import ColorPicker from './common/color-picker.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const defaultCard = type => ({
  image: { src: '', alt: type === 'good' ? 'UpAiry training underwear' : 'Disposable diaper' },
  title:
    type === 'good'
      ? '<h2 style="text-align:center;"><strong>Title</strong></h2>'
      : '<h2 style="text-align:center;">Title</h2>',
  items: type === 'good' ? ['<p>Content</p>'] : ['<p>Content</p>'],
  backgroundColor: type === 'good' ? '#4D903F' : '#EAEAEA',
  titleColor: type === 'good' ? '#FFFFFF' : '#242424',
  itemBackgroundColor: '#FFFFFF',
  itemTextColor: '#242424',
});

const defaultButton = {
  show: true,
  backgroundColor: '#4B963E',
  color: '#FFFFFF',
  radius: 16,
  text: '👉 START POTTY TRAINING TODAY',
  link: '#',
  iconPrefix: { show: false, src: '' },
  iconSuffix: { show: false, src: '' },
  subBtn: { show: false, text: '' },
};

export default {
  name: 'UpairyCompareCard',
  components: { UploadImage, ButtonPrimary, ColorPicker, SectionSettingsContainerFuild },
  props: { sectionEditor: { type: Object, required: true } },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      positionOptions: [
        { text: 'Left', value: 'left' },
        { text: 'Right', value: 'right' },
      ],
      cardOptions: [
        { key: 'good', label: 'Good', defaultBackground: '#4D903F', defaultTitleColor: '#FFFFFF' },
        { key: 'bad', label: 'Bad', defaultBackground: '#EAEAEA', defaultTitleColor: '#242424' },
      ],
      fields: {
        title: 'Upairy compare card',
        enableAnchor: false,
        image: { show: true, position: 'left', src: '', alt: 'Child wearing UpAiry training underwear' },
        header: '',
        good: defaultCard('good'),
        bad: defaultCard('bad'),
        button: { ...defaultButton },
        footer: '',
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
      },
    };
  },
  mounted() {
    const extract = this.sectionEditor.sectionExtract || {};
    this.fields = {
      ...this.fields,
      ...extract,
      image: { ...this.fields.image, ...(extract.image || {}) },
      good: this.mergeCard('good', extract.good),
      bad: this.mergeCard('bad', extract.bad),
      button: {
        ...defaultButton,
        ...(extract.button || {}),
        iconPrefix: { ...defaultButton.iconPrefix, ...((extract.button || {}).iconPrefix || {}) },
        iconSuffix: { ...defaultButton.iconSuffix, ...((extract.button || {}).iconSuffix || {}) },
        subBtn: { ...defaultButton.subBtn, ...((extract.button || {}).subBtn || {}) },
      },
      settings: { ...this.fields.settings, ...(extract.settings || {}) },
      containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
    };
  },
  methods: {
    mergeCard(type, card) {
      const defaults = defaultCard(type);
      return {
        ...defaults,
        ...(card || {}),
        image: { ...defaults.image, ...((card || {}).image || {}) },
        items: Array.isArray(card?.items) ? card.items : defaults.items,
      };
    },
    updateCard(type, key, value) {
      this.$set(this.fields[type], key, value);
    },
    updateCardItem(type, index, value) {
      this.$set(this.fields[type].items, index, value);
    },
    handleAddCardItem(type) {
      this.fields[type].items.push('<p>New item</p>');
    },
    handleRemoveCardItem(type, index) {
      this.fields[type].items.splice(index, 1);
    },
    handleUploadImage(obj) {
      this.fields.image.src = obj?.imageSrc || '';
    },
    handleRemoveImage() {
      this.fields.image.src = '';
    },
    handleUploadCardImage(type, obj) {
      this.fields[type].image.src = obj?.imageSrc || '';
    },
    handleRemoveCardImage(type) {
      this.fields[type].image.src = '';
    },
    getItemLabel(value, index) {
      return (
        String(value || '')
          .replace(/<[^>]*>/g, '')
          .trim() || `Item ${index + 1}`
      );
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionUpairyCompareCard(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', { ...this.sectionEditor.section, title: this.fields.title, html });
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.editor-dialog {
  background-color: #fff;
}
::v-deep .ck-editor__editable_inline {
  min-height: 100px !important;
}
.label-input {
  display: block;
  margin-bottom: 10px;
  color: #000;
  font-size: 18px;
  font-weight: 500;
}
.group-item {
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}
.header-modal,
.footer-modal {
  position: sticky;
  left: 0;
  z-index: 999;
  padding: 0 16px;
  background: #fff;
  box-shadow: 0 0 12px #0000000f;
}
.header-modal {
  top: 0;
}
.footer-modal {
  bottom: 0;
}
</style>
